/**
 * CefBackend Implementation
 *
 * Backend implementation for the CEF (Chromium Embedded Framework) host — the
 * local-desktop replacement for the Tauri shell. The React app runs inside the
 * CEF off-screen-rendered browser; instead of Tauri `invoke()`, every IBackend
 * method round-trips to the headless `ateliercode-core` sidecar over a localhost
 * WebSocket, using the envelope defined in `host/docs/ipc-contract.md`:
 *
 *   request:  { id, domain, command, params }
 *   response: { id, ok: true, result } | { id, ok: false, error: { code, message } }
 *   event:    { type: "event", domain, event, data }    (server-pushed streams)
 *
 * Discovery: the CEF host exposes a `window.cefQuery({request:'{"type":"ipc-endpoint"}'})`
 * bridge (host/src/osr.h, ticket 023+024) that returns the contents of
 * `core-endpoint.json` = { port, protocolVersion, token }. CefBackend asks for
 * that, then opens `ws://127.0.0.1:<port>/ipc?token=<token>`.
 *
 * This mirrors `TauriBackend.ts` one-to-one: same classes, same method
 * signatures, same command names — only the transport differs. Streaming
 * surfaces (terminal data/exit/activity/pattern-match) arrive as `type:"event"`
 * frames and are re-exposed through the identical callback signatures.
 *
 * Backend SELECTION (wiring CefBackend into `initializeBackend`) is ticket 025;
 * this module only exports `isCefHost()` + `createCefBackend()` for 025 to use,
 * and does not touch the existing Tauri/WebRTC/Mock selection.
 */

import type {
  IBackend,
  IProjectBackend,
  CreateProjectFromZipInput,
  PickFileOptions,
  IFileBackend,
  ITranscriptionBackend,
  ISystemBackend,
  AgentModelInfo,
  LogEntry,
  ITerminalBackend,
  IPipelineBackend,
  PipelineConfig,
  Ticket,
  TicketState,
  Transcript,
  AgentRun,
  AgentProfiles,
  PipelineRun,
  WorktreeInfo,
  TicketDiff,
  Runner,
  RunnerResult,
  PipelineTemplate,
  BackendType,
  GeneratedProjectDetails,
  TerminalSessionData,
  IQueueBackend,
  QueueTicketSummary,
  QueueEpicSummary,
  QueueIdeaSummary,
  QueueRunInfo,
  IIdentityBackend,
  ICopilotIdentityBackend,
  CopilotIdentity,
  CopilotAuthStatus,
  IGitHubBackend,
  GitHubRepo,
  IWhatsAppBackend,
  WhatsAppStatus,
  WhatsAppChat,
  WhatsAppBinding,
  ITeamsBackend,
  TeamsStatus,
  TeamsChat,
  TeamsBinding,
  ISlackBackend,
  SlackStatus,
  SlackChat,
  SlackBinding,
  ChannelMember,
  ChannelAccess,
  IChannelsBackend,
  InstancesRegistry,
  IIssuesBackend,
  IssuesStatus,
  IssueWatcher,
  IssuesDiscoverResult,
  IssueActivityEntry,
  ClaudeIdentity,
  AuthStatus,
  IGroupBackend,
  ProjectGroup,
  IArtifactsBackend,
  ArtifactEntry,
} from './IBackend';
import type {
  Project,
  CreateProjectInput,
  UpdateProjectInput,
  AgentInfo,
  ProjectAnalysisResult,
  FileNode,
  FileIndexEntry,
  GitFileStatus,
  TranscriptionResult,
  WhisperConfig,
  EnhanceTranscriptionParams,
} from './types';
import type { ProjectStats } from '../../types/tauri';
import { logger } from '@/lib/logger';
import { createHostBrowserControl, type HostBrowserControl } from './cefBrowser';
import { createCefWindowControl, type WindowControl } from './windowControl';
import { uint8ToBase64, base64ToUint8 } from '@/lib/base64';

// ─────────────────────────────────────────────────────────────────────────────
// CEF bridge typings (window.cefQuery, installed by the renderer-side message
// router in host/src/osr.h::OsrRenderProcessHandler).
// ─────────────────────────────────────────────────────────────────────────────

interface CefQueryRequest {
  request: string;
  onSuccess: (response: string) => void;
  onFailure: (errorCode: number, errorMessage: string) => void;
  persistent?: boolean;
}

declare global {
  interface Window {
    cefQuery?: (query: CefQueryRequest) => number;
    cefQueryCancel?: (queryId: number) => void;
  }
}

/** True when running inside the CEF host (the JS↔native bridge is installed). */
export function isCefHost(): boolean {
  return typeof window !== 'undefined' && typeof window.cefQuery === 'function';
}

/**
 * Low-level OS-integration bridge call (tickets 041/066/039). Sends one
 * `window.cefQuery` with the given request object and resolves with the host's
 * success payload (a string) or rejects on failure. Distinct from the IPC
 * WebSocket — these are host-surface commands (notify, file dialogs, popout)
 * the headless core can't serve. Mirrors cefBrowser.ts::browserQuery.
 */
function hostQuery(payload: Record<string, unknown>): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (typeof window === 'undefined' || typeof window.cefQuery !== 'function') {
      reject(new Error('CefBackend: window.cefQuery is not available (not running in the CEF host)'));
      return;
    }
    window.cefQuery({
      request: JSON.stringify(payload),
      onSuccess: (response: string) => resolve(response),
      onFailure: (code: number, message: string) => {
        reject(new Error(`CefBackend: ${String(payload.type)} failed (${code}): ${message}`));
      },
    });
  });
}

/**
 * OS clipboard via the host. In windowless OSR the async navigator.clipboard API
 * is blocked ("Document is not focused"), so the terminal can't copy/paste. These
 * route through the host's GTK clipboard over the cefQuery bridge; text is base64
 * so newlines/quotes/unicode survive the host's json_field() un-escaping.
 */
export interface CefClipboard {
  writeText(text: string): Promise<void>;
  readText(): Promise<string>;
  /**
   * Clipboard IMAGE as a base64 PNG, or '' when the clipboard holds no image.
   * navigator.clipboard.read() can't be used for this — OSR blocks it with
   * "Document is not focused" — so the host reads the GTK clipboard for us.
   * Resolves '' (rather than throwing) on an older host that lacks the command,
   * so callers can simply fall through to a text paste.
   */
  readImage(): Promise<string>;
}

function createCefClipboard(query: typeof hostQuery): CefClipboard {
  return {
    async writeText(text: string): Promise<void> {
      const b64 = uint8ToBase64(new TextEncoder().encode(text));
      await query({ type: 'clipboard-write', data: b64 });
    },
    async readText(): Promise<string> {
      const b64 = await query({ type: 'clipboard-read' });
      if (!b64) return '';
      return new TextDecoder().decode(base64ToUint8(b64));
    },
    async readImage(): Promise<string> {
      try {
        return (await query({ type: 'clipboard-read-image' })) || '';
      } catch {
        return ''; // host predates the command — caller falls back to text
      }
    },
  };
}

/**
 * Host popout-window control (ticket 039). A popout is a SECOND OS window that
 * the CEF host opens with its own off-screen browser at the SPA popout route.
 * The `id` doubles as the windowLabel so popoutContext's window-label semantics
 * are preserved. This is the CEF analogue of the Tauri `popout_open` command;
 * BrowserTab/TerminalTab/FilesTab branch on the backend to call it.
 */
export interface HostPopoutControl {
  /** Open (or focus, if already open) a popout window for (id, kind, projectId). */
  open(id: string, kind: string, projectId: string): Promise<string>;
  /** Close a popout window by id. */
  close(id: string): Promise<void>;
}

export function createHostPopoutControl(): HostPopoutControl {
  return {
    async open(id, kind, projectId) {
      return hostQuery({ type: 'popout-open', id, kind, projectId });
    },
    async close(id) {
      await hostQuery({ type: 'popout-close', id });
    },
  };
}

/** Shape of `core-endpoint.json`, returned by the `ipc-endpoint` cefQuery. */
interface IpcEndpoint {
  port: number;
  protocolVersion: number;
  token: string;
}

/** The protocol version this client speaks; mismatch is a hard error. */
const CLIENT_PROTOCOL_VERSION = 1;

// ─────────────────────────────────────────────────────────────────────────────
// IpcClient — the single WS connection multiplexing every domain, with
// request/response correlation by id, an event-subscriber registry, and
// reconnect-on-drop. Shared by all sub-backends.
// ─────────────────────────────────────────────────────────────────────────────

interface PendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
}

type EventListener = (data: unknown) => void;

class IpcClient {
  private ws: WebSocket | null = null;
  private connecting: Promise<void> | null = null;
  private nextId = 1;
  private readonly pending = new Map<string, PendingRequest>();
  /** key = `${domain}.${event}` → set of listeners. */
  private readonly listeners = new Map<string, Set<EventListener>>();
  /** Domains a `subscribe` has been (or is being) sent for; resent on reconnect. */
  private readonly subscribedDomains = new Set<string>();
  private closedByUs = false;
  private reconnectDelayMs = 500;

  /** Resolve the core endpoint via the CEF bridge. */
  private getEndpoint(): Promise<IpcEndpoint> {
    return new Promise<IpcEndpoint>((resolve, reject) => {
      if (typeof window === 'undefined' || typeof window.cefQuery !== 'function') {
        reject(new Error('CefBackend: window.cefQuery is not available (not running in the CEF host)'));
        return;
      }
      window.cefQuery({
        request: JSON.stringify({ type: 'ipc-endpoint' }),
        onSuccess: (response: string) => {
          try {
            const ep = JSON.parse(response) as IpcEndpoint;
            if (typeof ep.port !== 'number' || typeof ep.token !== 'string') {
              reject(new Error(`CefBackend: malformed core-endpoint.json: ${response}`));
              return;
            }
            resolve(ep);
          } catch (e) {
            reject(new Error(`CefBackend: failed to parse core-endpoint.json: ${String(e)}`));
          }
        },
        onFailure: (code: number, message: string) => {
          reject(new Error(`CefBackend: ipc-endpoint query failed (${code}): ${message}`));
        },
      });
    });
  }

  /** Open the WS (idempotent — concurrent callers share one connect promise). */
  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.connecting) return this.connecting;

    this.closedByUs = false;
    this.connecting = (async () => {
      const ep = await this.getEndpoint();
      if (ep.protocolVersion !== CLIENT_PROTOCOL_VERSION) {
        throw new Error(
          `core protocol mismatch: core speaks v${ep.protocolVersion}, client speaks v${CLIENT_PROTOCOL_VERSION}`,
        );
      }
      const url = `ws://127.0.0.1:${ep.port}/ipc?token=${encodeURIComponent(ep.token)}`;
      await new Promise<void>((resolve, reject) => {
        const ws = new WebSocket(url);
        this.ws = ws;
        ws.onopen = () => {
          this.reconnectDelayMs = 500;
          logger.debug('CefBackend', `IPC WebSocket connected (${url})`);
          // Re-arm any streaming subscriptions that were live before a drop.
          for (const domain of this.subscribedDomains) {
            this.send({ id: this.allocId(), domain, command: 'subscribe', params: {} });
          }
          // Tell the app the core is connected so it can (re)fetch state — on
          // EVERY open, not just reconnects. The very first connect can lose its
          // mount-time load to a startup race: the headless core hadn't written
          // its endpoint yet, so the initial connect failed and loadProjects
          // rejected, leaving the main project list blank (while Settings →
          // Project Maintenance, which re-loads on open, showed everything).
          // Firing here re-runs the fetch once we're actually connected, which
          // fills the list with no manual reload. Idempotent — a redundant fetch
          // on a healthy first connect is harmless; also covers a restarted core
          // that came back with an empty in-memory list.
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ac:core-reconnected'));
          }
          resolve();
        };
        ws.onerror = () => {
          // onclose follows; surface the connect failure to the awaiting caller.
          reject(new Error(`CefBackend: WebSocket error connecting to ${url}`));
        };
        ws.onclose = () => {
          this.ws = null;
          // Fail every in-flight request so callers don't hang forever.
          for (const [, p] of this.pending) {
            p.reject(new Error('CefBackend: IPC connection closed'));
          }
          this.pending.clear();
          if (!this.closedByUs) this.scheduleReconnect();
        };
        ws.onmessage = (ev) => this.onMessage(ev.data);
      });
    })();

    try {
      await this.connecting;
    } catch (e) {
      // A failure BEFORE the WS opened — e.g. the headless core hasn't written
      // its endpoint file yet at startup, so getEndpoint() rejects — never
      // reaches ws.onclose, so nothing else would schedule a retry. The client
      // would stay permanently disconnected until some later call happened to
      // re-invoke connect() (exactly why opening Project Maintenance "fixed" the
      // empty main project list). Schedule our own retry so the connection
      // establishes as soon as the core is ready, then re-fetches via onopen.
      if (!this.closedByUs) this.scheduleReconnect();
      throw e;
    } finally {
      this.connecting = null;
    }
  }

  private scheduleReconnect(): void {
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 10_000);
    logger.debug('CefBackend', `IPC connection lost; reconnecting in ${delay}ms`);
    setTimeout(() => {
      this.connect().catch((e) => logger.warn('CefBackend', `reconnect failed: ${String(e)}`));
    }, delay);
  }

  private allocId(): string {
    return `c${this.nextId++}`;
  }

  private send(frame: { id: string; domain: string; command: string; params: unknown }): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('CefBackend: IPC not connected');
    }
    this.ws.send(JSON.stringify(frame));
  }

  private onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      logger.warn('CefBackend', `dropping unparsable IPC frame: ${String(raw).slice(0, 200)}`);
      return;
    }

    // Event frame: { type:"event", domain, event, data }
    if (msg.type === 'event') {
      const key = `${String(msg.domain)}.${String(msg.event)}`;
      const set = this.listeners.get(key);
      if (set) {
        for (const fn of set) {
          try {
            fn(msg.data);
          } catch (e) {
            logger.warn('CefBackend', `event listener for ${key} threw: ${String(e)}`);
          }
        }
      }
      return;
    }

    // Response frame: { id, ok, result | error }
    const id = msg.id;
    if (typeof id === 'string') {
      const pending = this.pending.get(id);
      if (!pending) return;
      this.pending.delete(id);
      if (msg.ok === true) {
        pending.resolve(msg.result);
      } else {
        const err = (msg.error ?? {}) as { code?: string; message?: string };
        pending.reject(new Error(`${err.code ?? 'INTERNAL'}: ${err.message ?? 'unknown core error'}`));
      }
    }
  }

  /** Send a request and resolve with its `result` (typed by the caller). */
  async request<T>(domain: string, command: string, params: Record<string, unknown> = {}): Promise<T> {
    await this.connect();
    const id = this.allocId();
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      try {
        this.send({ id, domain, command, params });
      } catch (e) {
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  /** Subscribe to a streaming domain (sends `<domain>.subscribe` once). */
  async ensureSubscribed(domain: string): Promise<void> {
    if (this.subscribedDomains.has(domain)) {
      await this.connect();
      return;
    }
    // AVOID DOUBLE-SUBSCRIBE: the onopen handler re-arms (sends `subscribe`) for
    // every domain in subscribedDomains. If the socket isn't open yet, we add the
    // domain to the set and let connect()/onopen send the subscribe — sending our
    // OWN subscribe too would register the domain TWICE on the core, which then
    // streams every event (notably terminal output) DOUBLED. Only send our own
    // subscribe when the socket is ALREADY open (onopen won't re-fire for it).
    const alreadyOpen = this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    this.subscribedDomains.add(domain);
    if (alreadyOpen) {
      await this.request<unknown>(domain, 'subscribe', {});
    } else {
      await this.connect(); // onopen re-arms this domain (now in the set) exactly once
    }
  }

  /** Register an event listener; returns an unsubscribe fn. */
  on(domain: string, event: string, fn: EventListener): () => void {
    const key = `${domain}.${event}`;
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(fn);
    return () => {
      set?.delete(fn);
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Project
// ─────────────────────────────────────────────────────────────────────────────

class CefProjectBackend implements IProjectBackend {
  constructor(private readonly ipc: IpcClient) {}

  async create(input: CreateProjectInput): Promise<Project> {
    return this.ipc.request<Project>('project', 'create_project', { input });
  }
  async listZips(folder: string): Promise<string[]> {
    return this.ipc.request<string[]>('project', 'list_zips', { folder });
  }
  async createFromZip(input: CreateProjectFromZipInput): Promise<Project> {
    return this.ipc.request<Project>('project', 'create_from_zip', { ...input });
  }
  async extractZip(zipPath: string, targetPath: string): Promise<string> {
    return this.ipc.request<string>('project', 'extract_zip', { zipPath, targetPath });
  }
  async list(): Promise<Project[]> {
    return this.ipc.request<Project[]>('project', 'get_projects', {});
  }
  async get(id: string): Promise<Project | null> {
    return this.ipc.request<Project | null>('project', 'get_project', { id });
  }
  async update(id: string, updates: UpdateProjectInput): Promise<Project> {
    return this.ipc.request<Project>('project', 'update_project', { id, updates });
  }
  async delete(id: string): Promise<boolean> {
    return this.ipc.request<boolean>('project', 'delete_project', { id });
  }
  async hasRecentActivity(id: string): Promise<boolean> {
    return this.ipc.request<boolean>('project', 'has_recent_activity', { id });
  }
  // File-based analysis (no AI) — registered on the core (ticket 014/038 family;
  // exposed for parity in 067 alongside the AI ops below).
  async analyze(path: string): Promise<ProjectAnalysisResult> {
    return this.ipc.request<ProjectAnalysisResult>('project', 'analyze_project_directory', { path });
  }
  // AI-backed project ops (ticket 067). The core runs the configured AI CLI
  // (Tauri-free `ai_service`) and returns the same shapes the Tauri commands do.
  async analyzeWithAI(path: string): Promise<ProjectAnalysisResult> {
    return this.ipc.request<ProjectAnalysisResult>('project', 'analyze_project_with_ai', { path });
  }
  async generateDetails(projectPath: string): Promise<GeneratedProjectDetails> {
    return this.ipc.request<GeneratedProjectDetails>('project', 'generate_project_details', { projectPath });
  }
  async generateRunConfigs(projectPath: string): Promise<{ name: string; command: string }[]> {
    return this.ipc.request<{ name: string; command: string }[]>('project', 'generate_run_configurations', {
      projectPath,
    });
  }
  async isAIServiceAvailable(): Promise<boolean> {
    return this.ipc.request<boolean>('project', 'is_ai_service_available', {});
  }
  async getStats(projectId: string): Promise<ProjectStats> {
    return this.ipc.request<ProjectStats>('project', 'get_project_stats', { projectId });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// File
// ─────────────────────────────────────────────────────────────────────────────

class CefFileBackend implements IFileBackend {
  constructor(private readonly ipc: IpcClient) {}

  // Host-surface (ticket 066): the native folder picker needs the host's GTK
  // file chooser (a real window), so it goes over the cefQuery bridge, NOT the
  // IPC sidecar. The host returns the chosen absolute path, or "" on cancel
  // (mapped to null here to match the IFileBackend contract).
  async pickFolder(): Promise<string | null> {
    const path = await hostQuery({ type: 'pick-folder', title: 'Select Project Folder' });
    return path && path.length > 0 ? path : null;
  }
  // Native file picker over the same cefQuery host bridge as pick-folder. The
  // host shows a GTK/Win/Mac open-file dialog with an optional extension filter
  // and returns the chosen absolute path (or "" on cancel → null here).
  async pickFile(options?: PickFileOptions): Promise<string | null> {
    const path = await hostQuery({
      type: 'pick-file',
      title: options?.title ?? 'Select File',
      filterName: options?.filterName ?? '',
      // The host reads this as a comma-separated string (json_field), not a JSON array.
      extensions: (options?.extensions ?? []).join(','),
    });
    return path && path.length > 0 ? path : null;
  }
  async readFile(projectId: string, filePath: string): Promise<string> {
    return this.ipc.request<string>('file', 'read_file_content', { projectId, filePath });
  }
  async getFolderChildren(projectId: string, folderPath: string, parentGitignored?: boolean): Promise<FileNode[]> {
    return this.ipc.request<FileNode[]>('file', 'get_folder_children', {
      projectId,
      folderPath,
      parentGitignored: parentGitignored ?? false,
    });
  }
  async readProjectFiles(projectId: string): Promise<FileNode[]> {
    return this.ipc.request<FileNode[]>('file', 'read_project_files', { projectId });
  }
  async crawlProjectTree(projectId: string): Promise<FileNode[]> {
    return this.ipc.request<FileNode[]>('file', 'crawl_project_tree', { projectId });
  }
  async indexProjectFiles(projectId: string): Promise<FileIndexEntry[]> {
    return this.ipc.request<FileIndexEntry[]>('file', 'index_project_files', { projectId });
  }
  async getGitStatus(projectId: string): Promise<GitFileStatus[]> {
    return this.ipc.request<GitFileStatus[]>('file', 'get_git_status', { projectId });
  }
  async writeFile(projectId: string, filePath: string, content: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'write_file_content', { projectId, filePath, content });
  }
  async readFileBase64(projectId: string, filePath: string): Promise<string> {
    return this.ipc.request<string>('file', 'read_file_base64', { projectId, filePath });
  }
  // Filesystem op the sidecar can do (ticket 038) — writes into
  // .ateliercode/attachments/; returns the project-relative path.
  async saveAttachment(projectId: string, fileName: string, data: string): Promise<string> {
    return this.ipc.request<string>('file', 'save_attachment', { projectId, fileName, data });
  }
  // Host-surface (ticket 066): revealing in the OS file manager needs the host
  // shell (xdg-open / Finder / explorer), so it goes over the cefQuery bridge.
  async revealInExplorer(path: string): Promise<void> {
    await hostQuery({ type: 'reveal', path });
  }
  // Open a path/URL with the OS default handler (xdg-open). Pass a file:// URL
  // for a local file so it opens in its default app. Backs the terminal link
  // provider's "external" route (paths outside the project). Reuses the existing
  // open-external cefQuery bridge (host/src/osr.h) — no host change needed.
  async openExternal(url: string): Promise<void> {
    await hostQuery({ type: 'open-external', url });
  }
  async renameFile(projectId: string, oldPath: string, newName: string): Promise<string> {
    return this.ipc.request<string>('file', 'rename_file', { projectId, oldPath, newName });
  }
  async deleteFile(projectId: string, filePath: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'delete_file', { projectId, filePath });
  }
  async createFile(projectId: string, parentPath: string, fileName: string): Promise<string> {
    return this.ipc.request<string>('file', 'create_file', { projectId, parentPath, fileName });
  }
  async createFolder(projectId: string, parentPath: string, folderName: string): Promise<string> {
    return this.ipc.request<string>('file', 'create_folder', { projectId, parentPath, folderName });
  }
  async pathExistsInProject(projectId: string, relativePath: string): Promise<boolean> {
    return this.ipc.request<boolean>('file', 'path_exists_in_project', { projectId, relativePath });
  }
  // Pure-filesystem ops the sidecar can do (ticket 038) — drag/drop external
  // copy + intra-project move. Returns the final project-relative path.
  async copyExternalIntoProject(
    projectId: string,
    sourcePath: string,
    destFolderRelative: string,
    conflictResolution: 'overwrite' | 'keepboth',
  ): Promise<string> {
    return this.ipc.request<string>('file', 'copy_external_into_project', {
      projectId,
      sourcePath,
      destFolderRelative,
      conflictResolution,
    });
  }
  async moveWithinProject(
    projectId: string,
    sourceRelative: string,
    destFolderRelative: string,
  ): Promise<string> {
    return this.ipc.request<string>('file', 'move_within_project', {
      projectId,
      sourceRelative,
      destFolderRelative,
    });
  }
  async initAteliercodeConfig(projectId: string, configJson: string, runnersJson: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'init_ateliercode_config', { projectId, configJson, runnersJson });
  }
  async readAteliercodeRunners(projectId: string): Promise<string | null> {
    return this.ipc.request<string | null>('file', 'read_ateliercode_runners', { projectId });
  }
  async writeAteliercodeRunners(projectId: string, content: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'write_ateliercode_runners', { projectId, content });
  }
  async readAteliercodeConfig(projectId: string): Promise<string | null> {
    return this.ipc.request<string | null>('file', 'read_ateliercode_config', { projectId });
  }
  async writeAteliercodeConfig(projectId: string, content: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'write_ateliercode_config', { projectId, content });
  }
  async cacheRead(projectId: string, key: string): Promise<string | null> {
    return this.ipc.request<string | null>('file', 'cache_read', { projectId, key });
  }
  async cacheWrite(projectId: string, key: string, content: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'cache_write', { projectId, key, content });
  }
  async cacheClear(projectId: string): Promise<void> {
    await this.ipc.request<unknown>('file', 'cache_clear', { projectId });
  }
  async cacheClearAll(): Promise<number> {
    return this.ipc.request<number>('file', 'cache_clear_all', {});
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Transcription
// ─────────────────────────────────────────────────────────────────────────────

class CefTranscriptionBackend implements ITranscriptionBackend {
  constructor(private readonly ipc: IpcClient) {}

  // OpenAI Whisper cloud transcription over the core's `transcription.*` domain
  // (ticket 067). The core returns { text, language, duration }; the UI uses text.
  async transcribeOpenAI(audioData: string, apiKey: string): Promise<TranscriptionResult> {
    const result = await this.ipc.request<{ text: string }>('transcription', 'transcribe_openai', {
      audioData,
      apiKey,
    });
    return { text: result.text };
  }

  // Whisper settings are read from the frontend settings store (a pure-frontend
  // operation) — identical to TauriBackend, no transport involved.
  async getWhisperSettings(): Promise<WhisperConfig> {
    const { useSettingsStore } = await import('../../stores/settingsStore');
    const whisper = useSettingsStore.getState().whisper;
    return {
      provider: whisper.provider,
      openaiApiKey: whisper.openaiApiKey,
      transcriptionMode: whisper.transcriptionMode,
      voiceEngineUrl: whisper.voiceEngineUrl,
      voiceEngineToken: whisper.voiceEngineToken,
      voiceEngineModel: whisper.voiceEngineModel,
      enhanceModelUrl: whisper.enhanceModelUrl,
      enhanceModelName: whisper.enhanceModelName,
      enhanceModelApiKey: whisper.enhanceModelApiKey,
    };
  }

  // Enhanced transcription clean-up via the local AI, over the core's
  // `transcription.enhance` handler (which POSTs to the OpenAI-compatible
  // endpoint). Returns the cleaned text.
  async enhanceTranscription(params: EnhanceTranscriptionParams): Promise<TranscriptionResult> {
    const result = await this.ipc.request<{ text: string }>('transcription', 'enhance', {
      text: params.text,
      baseUrl: params.baseUrl,
      model: params.model,
      apiKey: params.apiKey ?? null,
      projectName: params.projectName ?? null,
      projectDescription: params.projectDescription ?? null,
    });
    return { text: result.text };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// System
// ─────────────────────────────────────────────────────────────────────────────

class CefSystemBackend implements ISystemBackend {
  constructor(private readonly ipc: IpcClient) {}

  async detectAgents(): Promise<AgentInfo[]> {
    return this.ipc.request<AgentInfo[]>('system', 'detect_agents', {});
  }
  // hostname/platform over the core's `system.*` domain (ticket 067). The core
  // runs on the same host, so these match the Tauri `get_hostname`/`get_platform`.
  async getHostname(): Promise<string> {
    return this.ipc.request<string>('system', 'get_hostname', {});
  }
  async getPlatform(): Promise<string> {
    return this.ipc.request<string>('system', 'get_platform', {});
  }
  async listAgentModels(agentType: string): Promise<AgentModelInfo[]> {
    return this.ipc.request<AgentModelInfo[]>('system', 'list_agent_models', { agentType });
  }
  // Application logs over the core's `system.read_logs` (restores the log viewer
  // the CEF/OSR migration left stubbed). undefined params are omitted by the IPC
  // envelope, so the core's defaults (all levels, 500 entries) apply.
  async readLogs(opts?: { level?: string; limit?: number }): Promise<LogEntry[]> {
    return this.ipc.request<LogEntry[]>('system', 'read_logs', {
      level: opts?.level,
      limit: opts?.limit,
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Terminal (streaming)
// ─────────────────────────────────────────────────────────────────────────────

class CefTerminalBackend implements ITerminalBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(): Promise<number[]> {
    return this.ipc.request<number[]>('terminal', 'list_terminals', {});
  }

  async listSessions(): Promise<TerminalSessionData[]> {
    const daemonSessions = await this.ipc.request<
      Array<{
        id: number;
        cwd: string | null;
        env: Record<string, string> | null;
        title: string;
        is_agent: boolean;
        attached: boolean;
        created_at: string;
      }>
    >('terminal', 'list_terminal_sessions', {});
    return daemonSessions.map((s) => {
      // Reconstitution stamps: the UI injects ATELIER_* vars at spawn and the
      // daemon persists the session env — read them back so adoption can match
      // by exact project id and rebuild the tab as what it originally was.
      const env = s.env ?? {};
      return {
        id: s.id,
        title: s.title,
        status: 'active' as const,
        projectPath: env.ATELIER_PROJECT_PATH ?? s.cwd ?? '__default__',
        isAgent: s.is_agent,
        projectId: env.ATELIER_PROJECT_ID,
        kind: env.ATELIER_SESSION_KIND,
        agentType: env.ATELIER_AGENT,
        launchCmd: env.ATELIER_LAUNCH_CMD,
        runConfig: env.ATELIER_RUN_CONFIG,
        claudeIdentity: env.ATELIER_CLAUDE_IDENTITY,
        createdAt: s.created_at,
        attached: s.attached,
      };
    });
  }

  async create(cols: number, rows: number, cwd?: string, _title?: string, shell?: string, env?: Record<string, string>): Promise<number> {
    // Ensure the PTY event stream is flowing before the session is created so
    // no early `terminal-data` frames are missed.
    await this.ipc.ensureSubscribed('terminal');
    return this.ipc.request<number>('terminal', 'create_terminal', {
      cols,
      rows,
      cwd: cwd ?? null,
      shell: shell ?? null,
      env: env ?? null,
    });
  }

  async write(id: number, data: string): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'write_terminal', { sessionId: id, data });
  }
  async resize(id: number, cols: number, rows: number): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'resize_terminal', { sessionId: id, cols, rows });
  }
  async close(id: number): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'close_terminal', { sessionId: id });
  }
  async detach(id: number): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'detach_terminal', { sessionId: id });
  }
  async attach(id: number): Promise<string> {
    await this.ipc.ensureSubscribed('terminal');
    return this.ipc.request<string>('terminal', 'attach_terminal', { sessionId: id });
  }
  async resume(id: number): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'resume_terminal', { sessionId: id });
  }
  async getBuffer(id: number): Promise<string> {
    return this.ipc.request<string>('terminal', 'get_terminal_buffer', { sessionId: id });
  }
  async markAgentSession(id: number, isAgent: boolean, completionPattern?: string): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'mark_agent_session', {
      sessionId: id,
      isAgent,
      completionPattern: completionPattern ?? null,
    });
  }

  onData(callback: (id: number, data: string) => void): () => void {
    void this.ipc.ensureSubscribed('terminal');
    return this.ipc.on('terminal', 'terminal-data', (data) => {
      const d = data as { id: number; data: string };
      callback(d.id, d.data);
    });
  }
  onExit(callback: (id: number) => void): () => void {
    void this.ipc.ensureSubscribed('terminal');
    return this.ipc.on('terminal', 'terminal-exit', (data) => {
      callback((data as { id: number }).id);
    });
  }
  onActivity(callback: (id: number) => void): () => void {
    void this.ipc.ensureSubscribed('terminal');
    return this.ipc.on('terminal', 'terminal-activity', (data) => {
      callback((data as { id: number }).id);
    });
  }
  onPatternMatch(callback: (id: number) => void): () => void {
    void this.ipc.ensureSubscribed('terminal');
    return this.ipc.on('terminal', 'terminal-pattern-match', (data) => {
      callback((data as { id: number }).id);
    });
  }

  async persistAll(): Promise<void> {
    await this.ipc.request<unknown>('terminal', 'persist_terminals', {});
  }

  async listAvailableShells(): Promise<{ id: string; name: string }[]> {
    return this.ipc.request<{ id: string; name: string }[]>('terminal', 'list_available_shells', {});
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline
// ─────────────────────────────────────────────────────────────────────────────

class CefPipelineBackend implements IPipelineBackend {
  constructor(private readonly ipc: IpcClient) {}

  async getConfig(projectId: string): Promise<PipelineConfig> {
    return this.ipc.request<PipelineConfig>('pipeline', 'get_pipeline_config', { projectId });
  }
  async setConfig(projectId: string, config: PipelineConfig): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'set_pipeline_config', { projectId, config });
  }
  async getPromptTemplate(projectId: string, name: string): Promise<string> {
    return this.ipc.request<string>('pipeline', 'get_prompt_template', { projectId, name });
  }
  async setPromptTemplate(projectId: string, name: string, content: string): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'set_prompt_template', { projectId, name, content });
  }
  async listTickets(projectId: string): Promise<Ticket[]> {
    return this.ipc.request<Ticket[]>('pipeline', 'list_tickets', { projectId });
  }
  async readTicket(projectId: string, ticketId: string): Promise<Ticket> {
    return this.ipc.request<Ticket>('pipeline', 'read_ticket', { projectId, ticketId });
  }
  async writeTicket(projectId: string, ticket: Ticket): Promise<Ticket> {
    return this.ipc.request<Ticket>('pipeline', 'write_ticket', { projectId, ticket });
  }
  async createTicket(
    projectId: string,
    title: string,
    body?: string,
    stage?: string,
    source?: string,
  ): Promise<Ticket> {
    return this.ipc.request<Ticket>('pipeline', 'create_ticket', {
      projectId,
      title,
      body: body ?? null,
      stage: stage ?? null,
      source: source ?? null,
    });
  }
  async deleteTicket(projectId: string, ticketId: string): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'delete_ticket', { projectId, ticketId });
  }
  async recordTranscript(
    projectId: string,
    source: Transcript['source'],
    title: string,
    content: string,
  ): Promise<Transcript> {
    return this.ipc.request<Transcript>('pipeline', 'record_transcript', { projectId, source, title, content });
  }
  async listTranscripts(projectId: string): Promise<Transcript[]> {
    return this.ipc.request<Transcript[]>('pipeline', 'list_transcripts', { projectId });
  }
  async readTranscript(projectId: string, name: string): Promise<string> {
    return this.ipc.request<string>('pipeline', 'read_transcript', { projectId, name });
  }
  async getAgentProfiles(projectId: string): Promise<AgentProfiles> {
    return this.ipc.request<AgentProfiles>('pipeline', 'get_agent_profiles', { projectId });
  }
  async setAgentProfiles(projectId: string, profiles: AgentProfiles): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'set_agent_profiles', { projectId, profiles });
  }

  // ── Run / orchestration / worktree / runner / template ops (ticket 067).
  // These turned out to be pure DB + file + git + subprocess (no host-side
  // execution surface), so the core now exposes them as `pipeline.*` handlers —
  // making the Run button + QA runners work under CefBackend identically to
  // Tauri. (Agent run LAUNCH itself is the PTY layer, already over `terminal.*`.)
  async setTicketState(
    projectId: string,
    ticketId: string,
    state: TicketState,
    stage?: string,
  ): Promise<Ticket> {
    return this.ipc.request<Ticket>('pipeline', 'set_ticket_state', {
      projectId,
      ticketId,
      state,
      stage: stage ?? null,
    });
  }
  async advanceTicket(projectId: string, ticketId: string): Promise<Ticket> {
    return this.ipc.request<Ticket>('pipeline', 'advance_ticket', { projectId, ticketId });
  }
  async prepareStageRun(projectId: string, ticketId: string, agentOverride?: string): Promise<AgentRun> {
    return this.ipc.request<AgentRun>('pipeline', 'prepare_stage_run', {
      projectId,
      ticketId,
      agentOverride: agentOverride ?? null,
    });
  }
  async prepareTriageRun(
    projectId: string,
    transcriptName: string,
    agentOverride?: string,
  ): Promise<AgentRun> {
    return this.ipc.request<AgentRun>('pipeline', 'prepare_triage_run', {
      projectId,
      transcriptName,
      agentOverride: agentOverride ?? null,
    });
  }
  async recordRunStart(input: {
    projectId: string;
    ticketId?: string;
    transcriptName?: string;
    stageId: string;
    kind: 'stage' | 'triage';
    agent?: string;
    ptySessionId?: number;
    prompt?: string;
  }): Promise<string> {
    return this.ipc.request<string>('pipeline', 'record_run_start', {
      projectId: input.projectId,
      ticketId: input.ticketId ?? null,
      transcriptName: input.transcriptName ?? null,
      stageId: input.stageId,
      kind: input.kind,
      agent: input.agent ?? null,
      ptySessionId: input.ptySessionId ?? null,
      prompt: input.prompt ?? null,
    });
  }
  async recordRunEnd(
    runId: string,
    status: PipelineRun['status'],
    exitCode?: number,
    cost?: string,
  ): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'record_run_end', {
      runId,
      status,
      exitCode: exitCode ?? null,
      cost: cost ?? null,
    });
  }
  async listRuns(projectId: string, ticketId?: string): Promise<PipelineRun[]> {
    return this.ipc.request<PipelineRun[]>('pipeline', 'list_runs', {
      projectId,
      ticketId: ticketId ?? null,
    });
  }
  async listActiveRuns(projectId: string): Promise<PipelineRun[]> {
    return this.ipc.request<PipelineRun[]>('pipeline', 'list_active_runs', { projectId });
  }
  async ensureTicketWorktree(projectId: string, ticketId: string): Promise<WorktreeInfo> {
    return this.ipc.request<WorktreeInfo>('pipeline', 'ensure_ticket_worktree', { projectId, ticketId });
  }
  async ticketDiff(projectId: string, ticketId: string): Promise<TicketDiff> {
    return this.ipc.request<TicketDiff>('pipeline', 'ticket_diff', { projectId, ticketId });
  }
  async openTicketPr(projectId: string, ticketId: string, title: string, body: string): Promise<string> {
    return this.ipc.request<string>('pipeline', 'open_ticket_pr', { projectId, ticketId, title, body });
  }
  async removeTicketWorktree(projectId: string, ticketId: string): Promise<void> {
    await this.ipc.request<unknown>('pipeline', 'remove_ticket_worktree', { projectId, ticketId });
  }
  async listRunners(projectId: string): Promise<Runner[]> {
    return this.ipc.request<Runner[]>('pipeline', 'list_runners', { projectId });
  }
  async generateQaRunners(projectPath: string): Promise<Runner[]> {
    return this.ipc.request<Runner[]>('pipeline', 'generate_qa_runners', { projectPath });
  }
  async runRunner(projectId: string, ticketId: string, runnerId: string): Promise<RunnerResult> {
    return this.ipc.request<RunnerResult>('pipeline', 'run_runner', { projectId, ticketId, runnerId });
  }
  async readRunLog(projectId: string, runId: string): Promise<string> {
    return this.ipc.request<string>('pipeline', 'read_run_log', { projectId, runId });
  }
  async exportPipelineTemplate(projectId: string): Promise<PipelineTemplate> {
    return this.ipc.request<PipelineTemplate>('pipeline', 'export_pipeline_template', { projectId });
  }
  async importPipelineTemplate(projectId: string, template: PipelineTemplate): Promise<PipelineConfig> {
    return this.ipc.request<PipelineConfig>('pipeline', 'import_pipeline_template', { projectId, template });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Queue (kanban + inbox + streamed script runs)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Queue backend over the IPC transport (ticket 065). The request/response
 * commands map to the `queue.*`/`inbox.*` core handlers; the streamed run maps
 * to `queue.start_run`/`queue.stop_run` plus the `queue.queue-output` /
 * `queue.queue-run-exit` event frames. Like the terminal backend, the run-event
 * subscriptions `ensureSubscribed('queue')` so frames flow (and re-arm on
 * reconnect) — the callback signatures match TauriQueueBackend exactly.
 */
class CefQueueBackend implements IQueueBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(projectId: string, folder: string): Promise<QueueTicketSummary[]> {
    return this.ipc.request<QueueTicketSummary[]>('queue', 'list', { projectId, folder });
  }
  async readTicket(projectId: string, folder: string, filename: string): Promise<string> {
    return this.ipc.request<string>('queue', 'read_ticket', { projectId, folder, filename });
  }
  async readLog(projectId: string, ticket: string): Promise<string> {
    return this.ipc.request<string>('queue', 'read_log', { projectId, ticket });
  }
  async moveTicket(projectId: string, from: string, to: string, filename: string): Promise<void> {
    await this.ipc.request<unknown>('queue', 'move_ticket', { projectId, from, to, filename });
  }

  async listEpics(projectId: string): Promise<QueueEpicSummary[]> {
    return this.ipc.request<QueueEpicSummary[]>('queue', 'list_epics', { projectId });
  }
  async setEngine(projectId: string, folder: string, filename: string, engine: 'local' | 'claude'): Promise<void> {
    await this.ipc.request<unknown>('queue', 'set_engine', { projectId, folder, filename, engine });
  }

  async submitFeatureRequest(projectId: string, title: string, body: string, kind?: string): Promise<{ id: string }> {
    return this.ipc.request<{ id: string }>('queue', 'submit_request', { projectId, title, body, kind });
  }
  async inboxList(projectId: string): Promise<QueueIdeaSummary[]> {
    return this.ipc.request<QueueIdeaSummary[]>('inbox', 'list', { projectId });
  }
  async inboxCreate(projectId: string, text: string): Promise<QueueIdeaSummary> {
    return this.ipc.request<QueueIdeaSummary>('inbox', 'create', { projectId, text });
  }
  async inboxUpdate(projectId: string, filename: string, text: string): Promise<void> {
    await this.ipc.request<unknown>('inbox', 'update', { projectId, filename, text });
  }
  async inboxDelete(projectId: string, filename: string): Promise<void> {
    await this.ipc.request<unknown>('inbox', 'delete', { projectId, filename });
  }
  async inboxLinkTicket(projectId: string, filename: string, ticketFilename: string): Promise<void> {
    await this.ipc.request<unknown>('inbox', 'link_ticket', { projectId, filename, ticketFilename });
  }
  async inboxArchive(projectId: string, filename: string): Promise<void> {
    await this.ipc.request<unknown>('inbox', 'archive', { projectId, filename });
  }

  async invokeScript(projectId: string, verb: string, extraArg?: string): Promise<string> {
    return this.ipc.request<string>('queue', 'invoke_script', {
      projectId,
      verb,
      extraArg: extraArg ?? null,
    });
  }
  async startRun(projectId: string, verb: string, extraArg?: string): Promise<void> {
    // Arm the queue event stream before the run starts so no early output frames
    // are missed (mirrors the terminal create path).
    await this.ipc.ensureSubscribed('queue');
    await this.ipc.request<unknown>('queue', 'start_run', {
      projectId,
      verb,
      extraArg: extraArg ?? null,
    });
  }
  async stopRun(): Promise<void> {
    await this.ipc.request<unknown>('queue', 'stop_run', {});
  }
  async listRuns(): Promise<QueueRunInfo[]> {
    return this.ipc.request<QueueRunInfo[]>('queue', 'list_runs', {});
  }

  onRunOutput(callback: (stream: string, line: string) => void): () => void {
    void this.ipc.ensureSubscribed('queue');
    return this.ipc.on('queue', 'queue-output', (data) => {
      const d = data as { stream: string; line: string };
      callback(d.stream, d.line);
    });
  }
  onRunExit(callback: (code: number | null) => void): () => void {
    void this.ipc.ensureSubscribed('queue');
    return this.ipc.on('queue', 'queue-run-exit', (data) => {
      callback((data as { code: number | null }).code);
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main CefBackend
// ─────────────────────────────────────────────────────────────────────────────

/** Raw `claude_identities` row as core serializes it (snake_case on the wire). */
interface RawIdentity {
  name: string;
  config_dir: string;
  email?: string | null;
  sso: boolean;
  is_default: boolean;
  created_at: number;
}
interface RawAuthStatus {
  authenticated: boolean;
  expires_at?: number | null;
  subscription_type?: string | null;
}

function mapIdentity(r: RawIdentity): ClaudeIdentity {
  return {
    name: r.name,
    configDir: r.config_dir,
    email: r.email ?? null,
    sso: r.sso,
    isDefault: r.is_default,
    createdAt: r.created_at,
  };
}

/** `identity.*` commands over the IPC WebSocket (core owns the identities DB). */
class CefIdentityBackend implements IIdentityBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(): Promise<ClaudeIdentity[]> {
    const rows = await this.ipc.request<RawIdentity[]>('identity', 'list', {});
    return (rows ?? []).map(mapIdentity);
  }

  async upsert(identity: { name: string; configDir: string; email?: string; sso?: boolean }): Promise<void> {
    await this.ipc.request<unknown>('identity', 'upsert', {
      name: identity.name,
      configDir: identity.configDir,
      email: identity.email ?? null,
      sso: identity.sso ?? false,
    });
  }

  async remove(name: string): Promise<void> {
    await this.ipc.request<unknown>('identity', 'delete', { name });
  }

  async setDefault(name: string): Promise<void> {
    await this.ipc.request<unknown>('identity', 'set_default', { name });
  }

  async getDefault(): Promise<ClaudeIdentity | null> {
    const row = await this.ipc.request<RawIdentity | null>('identity', 'get_default', {});
    return row ? mapIdentity(row) : null;
  }

  async status(configDir: string): Promise<AuthStatus> {
    const s = await this.ipc.request<RawAuthStatus>('identity', 'status', { configDir });
    return {
      authenticated: s.authenticated,
      expiresAt: s.expires_at ?? null,
      subscriptionType: s.subscription_type ?? null,
    };
  }
}

/** Raw `copilot_identities` row as core serializes it (snake_case on the wire). */
interface RawCopilotIdentity {
  name: string;
  home_dir: string;
  account?: string | null;
  is_default: boolean;
  created_at: number;
}

function mapCopilotIdentity(r: RawCopilotIdentity): CopilotIdentity {
  return {
    name: r.name,
    homeDir: r.home_dir,
    account: r.account ?? null,
    isDefault: r.is_default,
    createdAt: r.created_at,
  };
}

/** `copilotIdentity.*` commands over the IPC WebSocket (core owns the DB). */
class CefCopilotIdentityBackend implements ICopilotIdentityBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(): Promise<CopilotIdentity[]> {
    const rows = await this.ipc.request<RawCopilotIdentity[]>('copilotIdentity', 'list', {});
    return (rows ?? []).map(mapCopilotIdentity);
  }

  async upsert(identity: { name: string; homeDir: string; account?: string }): Promise<void> {
    await this.ipc.request<unknown>('copilotIdentity', 'upsert', {
      name: identity.name,
      homeDir: identity.homeDir,
      account: identity.account ?? null,
    });
  }

  async remove(name: string): Promise<void> {
    await this.ipc.request<unknown>('copilotIdentity', 'delete', { name });
  }

  async setDefault(name: string): Promise<void> {
    await this.ipc.request<unknown>('copilotIdentity', 'set_default', { name });
  }

  async getDefault(): Promise<CopilotIdentity | null> {
    const row = await this.ipc.request<RawCopilotIdentity | null>('copilotIdentity', 'get_default', {});
    return row ? mapCopilotIdentity(row) : null;
  }

  async status(homeDir: string): Promise<CopilotAuthStatus> {
    const s = await this.ipc.request<{ initialized: boolean }>('copilotIdentity', 'status', { homeDir });
    return { initialized: !!s.initialized };
  }
}

/** Raw `project_groups` row as core serializes it (snake_case on the wire). */
interface RawGroup {
  id: string;
  name: string;
  color?: string | null;
  sort_order: number;
  created_at: number;
}

function mapGroup(r: RawGroup): ProjectGroup {
  return {
    id: r.id,
    name: r.name,
    color: r.color ?? null,
    sortOrder: r.sort_order,
    createdAt: r.created_at,
  };
}

/** `group.*` commands over the IPC WebSocket (core owns the project_groups DB). */
class CefGroupBackend implements IGroupBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(): Promise<ProjectGroup[]> {
    const rows = await this.ipc.request<RawGroup[]>('group', 'list', {});
    return (rows ?? []).map(mapGroup);
  }

  async upsert(group: { id: string; name: string; color?: string | null; sortOrder?: number }): Promise<ProjectGroup> {
    const row = await this.ipc.request<RawGroup>('group', 'upsert', {
      id: group.id,
      name: group.name,
      color: group.color ?? null,
      sortOrder: group.sortOrder,
    });
    return mapGroup(row);
  }

  async remove(id: string): Promise<void> {
    await this.ipc.request<unknown>('group', 'delete', { id });
  }

  async reorder(ids: string[]): Promise<void> {
    await this.ipc.request<unknown>('group', 'reorder', { ids });
  }
}

class CefArtifactsBackend implements IArtifactsBackend {
  constructor(private readonly ipc: IpcClient) {}

  async list(projectId: string): Promise<ArtifactEntry[]> {
    const rows = await this.ipc.request<RawArtifact[]>('artifacts', 'list', { projectId });
    return (rows ?? []).map(mapArtifact);
  }
}

interface RawArtifact {
  url: string;
  artifact_id: string;
  label: string;
  created_at?: string | null;
  session_id?: string | null;
}

function mapArtifact(r: RawArtifact): ArtifactEntry {
  return {
    url: r.url,
    artifactId: r.artifact_id,
    label: r.label,
    createdAt: r.created_at ?? null,
    sessionId: r.session_id ?? null,
  };
}

class CefGitHubBackend implements IGitHubBackend {
  constructor(private readonly ipc: IpcClient) {}

  async listRepos(): Promise<GitHubRepo[]> {
    return await this.ipc.request<GitHubRepo[]>('github', 'list_repos', {});
  }

  async clone(repo: string, dest: string): Promise<{ path: string }> {
    return await this.ipc.request<{ path: string }>('github', 'clone', { repo, dest });
  }
}

class CefWhatsAppBackend implements IWhatsAppBackend {
  constructor(private readonly ipc: IpcClient) {}

  async status(instance?: string): Promise<WhatsAppStatus> {
    return await this.ipc.request<WhatsAppStatus>('whatsapp', 'status', { instance });
  }
  async listChats(instance?: string): Promise<{ ok: boolean; groups: WhatsAppChat[]; error?: string }> {
    return await this.ipc.request('whatsapp', 'list_chats', { instance });
  }
  async listBindings(projectRoot: string, instance?: string): Promise<{ whatsapp: WhatsAppBinding[] }> {
    return await this.ipc.request('whatsapp', 'list_bindings', { projectRoot, instance });
  }
  async bind(projectRoot: string, chat: string, opts: { project?: string; label?: string; signature?: string; watch?: boolean; instance?: string } = {}): Promise<{ ok: boolean }> {
    return await this.ipc.request('whatsapp', 'bind', { projectRoot, chat, ...opts });
  }
  async unbind(projectRoot: string, chat: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('whatsapp', 'unbind', { projectRoot, chat, instance });
  }
  async setIdentity(name: string, userId?: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('whatsapp', 'set_identity', { name, userId, instance });
  }
  async send(projectRoot: string, chat: string, msg: { text?: string; image?: string; caption?: string; file?: string; files?: string[]; instance?: string }): Promise<{ ok: boolean; id?: string; error?: string }> {
    return await this.ipc.request('whatsapp', 'send', { projectRoot, chat, ...msg });
  }
  async listMembers(chat: string, instance?: string): Promise<{ ok: boolean; members: ChannelMember[]; error?: string }> {
    return await this.ipc.request('whatsapp', 'list_members', { chat, instance });
  }
  async setMemberAccess(projectRoot: string, chat: string, memberId: string, access: ChannelAccess | '__clear__', instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('whatsapp', 'set_member_access', { projectRoot, chat, memberId, access, instance });
  }
  async setDefaultAccess(projectRoot: string, chat: string, access: ChannelAccess, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('whatsapp', 'set_default_access', { projectRoot, chat, access, instance });
  }
}

class CefTeamsBackend implements ITeamsBackend {
  constructor(private readonly ipc: IpcClient) {}

  async status(instance?: string): Promise<TeamsStatus> {
    return await this.ipc.request<TeamsStatus>('teams', 'status', { instance });
  }
  async listChats(instance?: string): Promise<{ ok: boolean; chats: TeamsChat[]; error?: string }> {
    return await this.ipc.request('teams', 'list_chats', { instance });
  }
  async relink(instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'relink', { instance });
  }
  async listBindings(projectRoot: string, instance?: string): Promise<{ teams: TeamsBinding[] }> {
    return await this.ipc.request('teams', 'list_bindings', { projectRoot, instance });
  }
  async bind(projectRoot: string, chat: string, opts: { project?: string; label?: string; kind?: string; teamId?: string; signature?: string; watch?: boolean; assist?: boolean; instance?: string } = {}): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'bind', { projectRoot, chat, ...opts });
  }
  async unbind(projectRoot: string, chat: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'unbind', { projectRoot, chat, instance });
  }
  async setIdentity(name: string, userId?: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'set_identity', { name, userId, instance });
  }
  async send(projectRoot: string, chat: string, msg: { text?: string; thread?: string; file?: string; files?: string[]; instance?: string }): Promise<{ ok: boolean; id?: string; error?: string }> {
    return await this.ipc.request('teams', 'send', { projectRoot, chat, ...msg });
  }
  async listMembers(chat: string, instance?: string): Promise<{ ok: boolean; members: ChannelMember[]; error?: string }> {
    return await this.ipc.request('teams', 'list_members', { chat, instance });
  }
  async setMemberAccess(projectRoot: string, chat: string, memberId: string, access: ChannelAccess | '__clear__', instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'set_member_access', { projectRoot, chat, memberId, access, instance });
  }
  async setDefaultAccess(projectRoot: string, chat: string, access: ChannelAccess, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('teams', 'set_default_access', { projectRoot, chat, access, instance });
  }
}

class CefSlackBackend implements ISlackBackend {
  constructor(private readonly ipc: IpcClient) {}

  async status(instance?: string): Promise<SlackStatus> {
    return await this.ipc.request<SlackStatus>('slack', 'status', { instance });
  }
  async listChats(instance?: string): Promise<{ ok: boolean; chats: SlackChat[]; error?: string }> {
    return await this.ipc.request('slack', 'list_chats', { instance });
  }
  async listBindings(projectRoot: string, instance?: string): Promise<{ slack: SlackBinding[] }> {
    return await this.ipc.request('slack', 'list_bindings', { projectRoot, instance });
  }
  async bind(projectRoot: string, chat: string, opts: { project?: string; label?: string; signature?: string; watch?: boolean; assist?: boolean; instance?: string } = {}): Promise<{ ok: boolean }> {
    return await this.ipc.request('slack', 'bind', { projectRoot, chat, ...opts });
  }
  async unbind(projectRoot: string, chat: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('slack', 'unbind', { projectRoot, chat, instance });
  }
  async setIdentity(name: string, userId?: string, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('slack', 'set_identity', { name, userId, instance });
  }
  async send(projectRoot: string, chat: string, msg: { text?: string; thread?: string; file?: string; files?: string[]; instance?: string }): Promise<{ ok: boolean; id?: string; error?: string }> {
    return await this.ipc.request('slack', 'send', { projectRoot, chat, ...msg });
  }
  async listMembers(chat: string, instance?: string): Promise<{ ok: boolean; members: ChannelMember[]; error?: string }> {
    return await this.ipc.request('slack', 'list_members', { chat, instance });
  }
  async setMemberAccess(projectRoot: string, chat: string, memberId: string, access: ChannelAccess | '__clear__', instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('slack', 'set_member_access', { projectRoot, chat, memberId, access, instance });
  }
  async setDefaultAccess(projectRoot: string, chat: string, access: ChannelAccess, instance?: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('slack', 'set_default_access', { projectRoot, chat, access, instance });
  }
}

class CefChannelsBackend implements IChannelsBackend {
  constructor(private readonly ipc: IpcClient) {}
  async listInstances(provider?: string): Promise<{ ok: boolean; registry?: InstancesRegistry; provider?: string }> {
    return await this.ipc.request('channels', 'list_instances', { provider });
  }
  async addInstance(provider: string, name: string): Promise<{ ok: boolean; id?: string; stateDir?: string }> {
    return await this.ipc.request('channels', 'add_instance', { provider, name });
  }
  async removeInstance(provider: string, instance: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('channels', 'remove_instance', { provider, instance });
  }
  async setDefault(provider: string, instance: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('channels', 'set_default', { provider, instance });
  }
  async connectInstance(provider: string, instance: string): Promise<{ ok: boolean; output?: string }> {
    return await this.ipc.request('channels', 'connect_instance', { provider, instance });
  }
  async instanceStatus(provider: string, instance: string): Promise<{ ok: boolean; active?: string }> {
    return await this.ipc.request('channels', 'instance_status', { provider, instance });
  }
}

class CefIssuesBackend implements IIssuesBackend {
  constructor(private readonly ipc: IpcClient) {}

  async status(): Promise<IssuesStatus> {
    return await this.ipc.request<IssuesStatus>('issues', 'status', {});
  }
  async discover(owner?: string): Promise<IssuesDiscoverResult> {
    return await this.ipc.request('issues', 'discover', { owner });
  }
  async listWatchers(): Promise<{ pollSeconds: number; watchers: IssueWatcher[] }> {
    return await this.ipc.request('issues', 'list_watchers', {});
  }
  async addWatcher(opts: { repo: string; projectRoot?: string; labels?: string[]; trustedAuthors?: string[]; resolveDepth?: string; requireLabel?: boolean }): Promise<{ ok: boolean; watcher: IssueWatcher }> {
    return await this.ipc.request('issues', 'add_watcher', { ...opts });
  }
  async setWatcher(opts: { repo: string; enabled?: boolean; labels?: string[]; trustedAuthors?: string[]; resolveDepth?: string; requireLabel?: boolean }): Promise<{ ok: boolean; watcher: IssueWatcher }> {
    return await this.ipc.request('issues', 'set_watcher', { ...opts });
  }
  async removeWatcher(repo: string): Promise<{ ok: boolean }> {
    return await this.ipc.request('issues', 'remove_watcher', { repo });
  }
  async activity(limit?: number): Promise<{ entries: IssueActivityEntry[] }> {
    return await this.ipc.request('issues', 'activity', { limit });
  }
}

export class CefBackend implements IBackend {
  readonly type: BackendType = 'tauri'; // local-desktop backend; same surface as Tauri
  readonly isReady: boolean = true;

  readonly projects: IProjectBackend;
  readonly files: IFileBackend;
  readonly artifacts: IArtifactsBackend;
  readonly transcription: ITranscriptionBackend;
  readonly system: ISystemBackend;
  readonly terminal: ITerminalBackend;
  readonly pipeline: IPipelineBackend;
  readonly queue: IQueueBackend;
  readonly identity: IIdentityBackend;
  readonly copilotIdentity: ICopilotIdentityBackend;
  readonly groups: IGroupBackend;
  readonly github: IGitHubBackend;
  readonly whatsapp: IWhatsAppBackend;
  readonly teams: ITeamsBackend;
  readonly slack: ISlackBackend;
  readonly channels: IChannelsBackend;
  readonly issues: IIssuesBackend;

  /**
   * Host-managed off-screen embedded browsers (027/028/029). Unlike the other
   * sub-backends this does NOT go over the IPC WebSocket — the browser registry
   * lives host-side and is reached via the `window.cefQuery` bridge. BrowserTab
   * drives it (create/navigate/setRect/show/hide/focus/close per tab) so the
   * host composites a real web page into the tab's content rect, replacing the
   * old Tauri child-webview overlay (031 deletes that path).
   */
  readonly browser: HostBrowserControl;

  /**
   * Host popout-window control (ticket 039). Opens a SECOND OS window with its
   * own OSR browser at the SPA popout route. The popout buttons in
   * BrowserTab/TerminalTab/FilesTab branch on the backend and call this in the
   * CEF host (vs the raw Tauri `popout_open` command on the Tauri shell).
   */
  readonly popout: HostPopoutControl;

  /**
   * Native main-window control for the custom titlebar (custom-titlebar
   * migration). The CEF host runs the main window FRAMELESS and the React app
   * draws its own titlebar; like `browser`/`popout` this is NOT over the IPC
   * WebSocket but over the `window.cefQuery` {type:"window",...} bridge.
   */
  readonly window: WindowControl;

  /** OS clipboard via the host (navigator.clipboard is blocked in OSR). */
  readonly clipboard: CefClipboard;

  private readonly ipc: IpcClient;

  constructor() {
    this.ipc = new IpcClient();
    this.projects = new CefProjectBackend(this.ipc);
    this.files = new CefFileBackend(this.ipc);
    this.artifacts = new CefArtifactsBackend(this.ipc);
    this.transcription = new CefTranscriptionBackend(this.ipc);
    this.system = new CefSystemBackend(this.ipc);
    this.terminal = new CefTerminalBackend(this.ipc);
    this.pipeline = new CefPipelineBackend(this.ipc);
    this.queue = new CefQueueBackend(this.ipc);
    this.identity = new CefIdentityBackend(this.ipc);
    this.copilotIdentity = new CefCopilotIdentityBackend(this.ipc);
    this.groups = new CefGroupBackend(this.ipc);
    this.github = new CefGitHubBackend(this.ipc);
    this.whatsapp = new CefWhatsAppBackend(this.ipc);
    this.teams = new CefTeamsBackend(this.ipc);
    this.slack = new CefSlackBackend(this.ipc);
    this.channels = new CefChannelsBackend(this.ipc);
    this.issues = new CefIssuesBackend(this.ipc);
    this.browser = createHostBrowserControl();
    this.popout = createHostPopoutControl();
    this.window = createCefWindowControl(hostQuery);
    this.clipboard = createCefClipboard(hostQuery);
  }

  /** Eagerly open the IPC connection (optional — requests connect lazily too). */
  async connect(): Promise<void> {
    await this.ipc.connect();
  }
}

/** Create a CefBackend if running inside the CEF host, else null. */
export function createCefBackend(): CefBackend | null {
  return isCefHost() ? new CefBackend() : null;
}
